import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import { Fragment, useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
  Create,
  FormDataConsumer,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  number,
  required,
  useGetList,
  useNotify,
  useRecordContext,
} from 'react-admin';
import DateTimeInputUTC from '../DateTimeInputUTC';

import { getChainId } from '../../lib';
import cleanEmpty from '../../lib/cleanEmpty';
import PriceInput from '../PriceInput';
import { CheckChainUsesValidation, ValidationRuleKey, SaveConfirmButton } from '../../components'
import { formatNumber, onlyNumbers } from '../../utils';

const VALUE_VALIDATION = [required(), number()]

const BalanceButton = ({ onClose }) => {
  const notify = useNotify();
  const record = useRecordContext();
  const [displayModal, setDisplayModal] = useState(true);

  const onSuccess = () => {
    notify('ra.notification.created');
    setDisplayModal(false);
    onClose();
  };

  const HoseInput = () => {
    const placeId = useWatch({ name: 'placeId' });
    const fuelId = useWatch({ name: 'fuelId' });

    // Sem posto e combustível a URL vira `stations/undefined/hose-numbers` (404 em prod).
    const { data: hoseNumber, isLoading: isPendingHose, isError } = useGetList(
      `stations/${placeId}/hose-numbers`,
      { filter: { fuelId } },
      { enabled: !!placeId && !!fuelId },
    );

    if (!placeId || !fuelId || (!isPendingHose && isError)) {
      return <></>
    }

    return (
      <CheckChainUsesValidation ruleKey={ValidationRuleKey.requiredHoseNumber}>
        <RadioButtonGroupInput
          source="hoseNumber"
          optionText="hoseNumber"
          label="Bicos"
          sx={{ maxWidth: 200 }}
          choices={hoseNumber || []}
          isLoading={isPendingHose}
        />
      </CheckChainUsesValidation>
    );
  }

  const CustomToolbar = () => {
    return (
      <Toolbar>
        <FormDataConsumer>
          {({ formData }) => (
            <SaveConfirmButton
              formData={formData}
              validate={(data) => {
                if (data.fuelId && data.fillinAmount > 0) {
                  const pricePerLiter = +(data.fillinValue / data.fillinAmount).toFixed(3);
                  return pricePerLiter > 10 || pricePerLiter < 1;
                }
                return false
              }}
              contentMessage={
                <Alert
                  variant="outlined"
                  severity="warning"
                >
                  O preço por litro é <strong>{formatNumber(+(formData.fillinValue / formData.fillinAmount).toFixed(3), { style: 'currency', currency: 'BRL', minimumFractionDigits: 3, maximumFractionDigits: 3 })}</strong> <br />
                  Este valor parece incorreto. Tem certeza que deseja continuar?
                </Alert>
              }
            />
          )}
        </FormDataConsumer>
      </Toolbar>
    )
  }

  return (
    <Fragment>
      <Dialog
        open={displayModal}
        onClose={() => { setDisplayModal(false); onClose(); }}
        aria-label="Adicionar saldo a um cliente"
      >
        <DialogTitle>{'Adicionar pontos para o cliente'}</DialogTitle>
        <DialogContent>
          <Create title="Adicionar saldo" transform={cleanEmpty} resource={`chains/chainId/customers/${record.id}/balance`} mutationOptions={{ onSuccess }}>
            <SimpleForm shouldUnregister sanitizeEmptyValues toolbar={<CustomToolbar />}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <NumberInput
                  autoFocus
                  step={1}
                  label="Pontos"
                  source="value"
                  validate={VALUE_VALIDATION}
                />
                <TextInput
                  label="Descrição"
                  source="description"
                />
                <ReferenceInput
                  source="placeId"
                  filter={{ chainId: getChainId() }}
                  reference="places"
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={9999}
                >
                  <SelectInput
                    optionText="name"
                    label="Posto"
                    validate={required()}
                  />
                </ReferenceInput>
                <ReferenceInput
                  source="fuelId"
                  reference="fuels"
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput
                    optionText="name"
                    label="Combustível"
                  />
                </ReferenceInput>
                <FormDataConsumer>
                  {({ formData }) => {
                    return formData.fuelId && (
                      <>
                        <PriceInput source='fillinValue' label='Valor em reais' validate={required()} />
                        <TextInput
                          source="fillinAmount"
                          label="Quantidade"
                          validate={required()}
                          format={(value) => {
                            if (!value || value === 0) {
                              return "0,00 l";
                            }
                            return `${Number(value).toFixed(2)} l`;
                          }}
                          parse={(value) => {
                            if (!value || value === '') {
                              return 0;
                            }
                            const numbers = value.indexOf('l') > -1 ? +onlyNumbers(value) : +onlyNumbers(value.slice(0, -2));
                            return numbers / 100;
                          }}
                        />
                        <HoseInput />
                        {CheckChainUsesValidation({
                          children: (
                            <NumberInput
                              source="encerrante"
                              label="Encerrante"
                            />
                          ),
                          ruleKey: ValidationRuleKey.requiredEncerrante
                        })}
                        <DateTimeInputUTC
                          source="createdAt"
                          label="Data e Hora"
                        />
                      </>
                    );
                  }}
                </FormDataConsumer>
              </div>
            </SimpleForm>
          </Create>
        </DialogContent>
      </Dialog>
    </Fragment >
  )
}

export default BalanceButton;
